// src/features/clients/components/ClientModal/index.jsx
import React, { useState, useEffect } from 'react';
import { createClient, updateClient, resetClientPassword } from '../../../../api';
import Modal, { ModalFooter } from '../../../../shared/components/ui/Modal';

const TABS = { info: 'البيانات الأساسية', password: 'كلمة المرور' };

function ClientModal({ isOpen, onClose, onSave, clientToEdit }) {
  const isEditing = !!clientToEdit;

  const [activeTab,  setActiveTab]  = useState('info');
  const [clientData, setClientData] = useState({ name: '', phone: '' });
  const [passwords,  setPasswords]  = useState({ new_password: '', confirm_password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');

  useEffect(() => {
    if (clientToEdit) {
      setClientData({ name: clientToEdit.name || '', phone: clientToEdit.phone || '' });
    } else {
      setClientData({ name: '', phone: '' });
    }
    setPasswords({ new_password: '', confirm_password: '' });
    setActiveTab('info');
    setError('');
    setSuccess('');
  }, [clientToEdit, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setClientData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords(prev => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    setClientData({ name: '', phone: '' });
    setPasswords({ new_password: '', confirm_password: '' });
    setError('');
    setSuccess('');
    onClose();
  };

  // ── حفظ البيانات الأساسية ────────────────────────────────
  const handleSaveInfo = async () => {
    setError('');
    setSuccess('');

    if (!clientData.name.trim()) {
      setError('اسم العميل مطلوب');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name:  clientData.name.trim(),
        phone: clientData.phone.trim() || null,
      };

      const response = isEditing
        ? await updateClient(clientToEdit.id, payload)
        : await createClient(payload);

      if (response.data?.id) {
        if (!isEditing) {
          setClientData({ name: '', phone: '' });
          onSave(response.data);
        } else {
          setSuccess('تم حفظ البيانات بنجاح.');
          onSave(response.data);
        }
      } else {
        setError('حدث خطأ في معالجة البيانات.');
      }
    } catch (err) {
      if (err.response?.data) {
        const errorData = err.response.data;
        if (typeof errorData === 'object') {
          const msg = Object.entries(errorData)
            .map(([field, messages]) => {
              const text = Array.isArray(messages) ? messages.join(' ') : messages;
              return `${field === 'phone' ? 'رقم الهاتف' : field}: ${text}`;
            })
            .join(' | ');
          setError(msg || (isEditing ? 'فشل في تعديل العميل.' : 'فشل في إضافة العميل.'));
        } else {
          setError(String(errorData));
        }
      } else {
        setError(err.message ? `خطأ في الاتصال: ${err.message}` : 'حدث خطأ غير متوقع.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── حفظ كلمة المرور ─────────────────────────────────────
  const handleSavePassword = async () => {
    setError('');
    setSuccess('');

    if (!passwords.new_password) {
      setError('يرجى إدخال كلمة المرور الجديدة.');
      return;
    }
    if (passwords.new_password.length < 6) {
      setError('كلمة المرور يجب أن تكون 6 أحرف على الأقل.');
      return;
    }
    if (passwords.new_password !== passwords.confirm_password) {
      setError('كلمتا المرور غير متطابقتين.');
      return;
    }

    setSubmitting(true);
    try {
      await resetClientPassword(clientToEdit.id, {
        new_password: passwords.new_password,
      });
      setPasswords({ new_password: '', confirm_password: '' });
      setSuccess('تم تغيير كلمة المرور بنجاح.');
    } catch (err) {
      const data = err.response?.data;
      setError(
        data?.new_password?.[0] ||
        data?.detail ||
        'فشل في تغيير كلمة المرور.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSave = () =>
    activeTab === 'info' ? handleSaveInfo() : handleSavePassword();

  const saveLabel = activeTab === 'info'
    ? (isEditing ? 'حفظ التعديلات' : 'حفظ العميل')
    : 'تغيير كلمة المرور';

  // ── styles مشتركة ────────────────────────────────────────
  const labelStyle = {
    display:    'block',
    marginBottom: 'var(--space-2)',
    fontWeight: 'var(--font-semibold)',
    fontSize:   'var(--font-size-sm)',
    color:      'var(--text-heading)',
  };

  const errorStyle = {
    color:        'var(--color-danger-text)',
    background:   'var(--color-danger-subtle)',
    border:       '1px solid var(--color-danger-muted)',
    borderRight:  '3px solid var(--color-danger)',
    padding:      'var(--space-3) var(--space-4)',
    borderRadius: 'var(--radius-md)',
    fontSize:     'var(--font-size-sm)',
    fontWeight:   'var(--font-semibold)',
    margin:       0,
  };

  const successStyle = {
    color:        'var(--color-success-text)',
    background:   'var(--color-success-subtle)',
    border:       '1px solid var(--color-success-muted)',
    borderRight:  '3px solid var(--color-success)',
    padding:      'var(--space-3) var(--space-4)',
    borderRadius: 'var(--radius-md)',
    fontSize:     'var(--font-size-sm)',
    fontWeight:   'var(--font-semibold)',
    margin:       0,
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? `تعديل العميل: ${clientToEdit.name}` : 'إضافة عميل جديد'}
      footer={
        <ModalFooter
          onClose={handleClose}
          onSave={handleSave}
          saveLabel={saveLabel}
          loading={submitting}
        />
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>

        {/* ── Tabs — تظهر فقط عند التعديل ── */}
        {isEditing && (
          <div style={{
            display:      'flex',
            borderBottom: '2px solid var(--border-default)',
            marginBottom: 'var(--space-2)',
          }}>
            {Object.entries(TABS).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => { setActiveTab(key); setError(''); setSuccess(''); }}
                style={{
                  padding:      'var(--space-3) var(--space-5)',
                  border:       'none',
                  borderBottom: activeTab === key
                    ? '2px solid var(--color-primary)'
                    : '2px solid transparent',
                  marginBottom: '-2px',
                  background:   'transparent',
                  color:        activeTab === key
                    ? 'var(--color-primary)'
                    : 'var(--text-muted)',
                  fontWeight:   activeTab === key
                    ? 'var(--font-bold)'
                    : 'var(--font-normal)',
                  fontSize:     'var(--font-size-sm)',
                  cursor:       'pointer',
                  transition:   'var(--transition-fast)',
                  fontFamily:   'var(--font-family)',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* ── تاب البيانات الأساسية ── */}
        {activeTab === 'info' && (
          <>
            <div>
              <label style={labelStyle}>اسم العميل *</label>
              <input
                type="text"
                name="name"
                value={clientData.name}
                onChange={handleChange}
                placeholder="أدخل اسم العميل"
                autoFocus
                disabled={submitting}
              />
            </div>

            <div>
              <label style={labelStyle}>رقم الهاتف</label>
              <input
                type="text"
                name="phone"
                value={clientData.phone}
                onChange={handleChange}
                placeholder="أدخل رقم الهاتف (اختياري)"
                disabled={submitting}
                dir="ltr"
              />
              {!isEditing && clientData.phone ? (
                <small style={{
                  display:      'block',
                  marginTop:    'var(--space-2)',
                  padding:      'var(--space-2) var(--space-3)',
                  background:   'var(--color-info-subtle)',
                  border:       '1px solid var(--color-info-border)',
                  borderRight:  '3px solid var(--color-info)',
                  borderRadius: 'var(--radius-md)',
                  color:        'var(--color-info-text)',
                  fontSize:     'var(--font-size-xs)',
                  fontWeight:   'var(--font-semibold)',
                }}>
                  سيُنشأ حساب دخول تلقائياً برقم الهاتف وكلمة مرور{' '}
                  <code style={{ background: 'var(--color-info-muted)', padding: '1px 5px', borderRadius: 'var(--radius-sm)' }}>
                    00000000
                  </code>
                </small>
              ) : (
                <small style={{ display: 'block', marginTop: 'var(--space-1)', color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>
                  اختياري — إذا تم إدخاله يجب أن يكون فريداً
                </small>
              )}
            </div>
          </>
        )}

        {/* ── تاب كلمة المرور ── */}
        {activeTab === 'password' && (
          <>
            <div style={{
              padding:      'var(--space-3) var(--space-4)',
              background:   'var(--color-warning-subtle)',
              border:       '1px solid var(--color-warning-muted)',
              borderRight:  '3px solid var(--color-warning)',
              borderRadius: 'var(--radius-md)',
              fontSize:     'var(--font-size-xs)',
              color:        'var(--color-warning-text)',
              fontWeight:   'var(--font-semibold)',
            }}>
              ⚠️ سيتم تغيير كلمة مرور حساب <strong>{clientToEdit?.name}</strong> — تأكد قبل الحفظ.
            </div>

            <div>
              <label style={labelStyle}>كلمة المرور الجديدة *</label>
              <input
                type="password"
                name="new_password"
                value={passwords.new_password}
                onChange={handlePasswordChange}
                placeholder="أدخل كلمة المرور الجديدة"
                disabled={submitting}
                dir="ltr"
                autoComplete="new-password"
              />
            </div>

            <div>
              <label style={labelStyle}>تأكيد كلمة المرور *</label>
              <input
                type="password"
                name="confirm_password"
                value={passwords.confirm_password}
                onChange={handlePasswordChange}
                placeholder="أعد إدخال كلمة المرور"
                disabled={submitting}
                dir="ltr"
                autoComplete="new-password"
              />
              {passwords.new_password && passwords.confirm_password && (
                <small style={{
                  display:    'block',
                  marginTop:  'var(--space-1)',
                  fontSize:   'var(--font-size-xs)',
                  fontWeight: 'var(--font-semibold)',
                  color:      passwords.new_password === passwords.confirm_password
                    ? 'var(--color-success-text)'
                    : 'var(--color-danger-text)',
                }}>
                  {passwords.new_password === passwords.confirm_password
                    ? '✓ كلمتا المرور متطابقتان'
                    : '✗ كلمتا المرور غير متطابقتين'}
                </small>
              )}
            </div>
          </>
        )}

        {/* ── رسائل ── */}
        {error   && <p style={errorStyle}>⚠️ {error}</p>}
        {success && <p style={successStyle}>✓ {success}</p>}

      </div>
    </Modal>
  );
}

export default ClientModal;